import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@hey404/ui";
import { inputClass } from "./admin-ui";

/**
 * The platform's own select, replacing the browser's native menu so the open
 * list follows the design system. A drop-in for `<select>`: it takes the same
 * `<option>` children, `name` (a hidden input carries the value into forms),
 * `value`/`defaultValue` and an `onChange` whose event exposes
 * `target.value`, so existing handlers keep working.
 *
 * Accessible as a select-only combobox: focus stays on the trigger, arrows
 * move through the options (aria-activedescendant), Enter/Space pick,
 * Escape/Tab close, Home/End jump, typing letters finds an option. The list
 * renders in a portal with fixed positioning so modals and tables never clip
 * it, and opens upwards when there's no room below.
 */

export interface SelectChangeEvent {
  target: { value: string; name?: string };
  currentTarget: { value: string; name?: string };
}

export interface SelectProps {
  children: ReactNode;
  name?: string;
  value?: string | number;
  defaultValue?: string | number | null;
  onChange?: (event: SelectChangeEvent) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  /** Classes for the wrapper; inline selects (inside text) pass `inline-flex`. */
  wrapperClassName?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

interface Option {
  value: string;
  label: ReactNode;
  text: string;
  disabled: boolean;
}

type OptionElement = ReactElement<{
  value?: string | number;
  children?: ReactNode;
  disabled?: boolean;
}>;

const textOf = (node: ReactNode): string =>
  Children.toArray(node)
    .map((child) =>
      typeof child === "string" || typeof child === "number"
        ? String(child)
        : isValidElement<{ children?: ReactNode }>(child)
          ? textOf(child.props.children)
          : "",
    )
    .join("");

export function Select({
  children,
  name,
  value,
  defaultValue,
  onChange,
  disabled,
  // Accepted for parity with <select>; the value is never empty-unset here.
  required: _required,
  id,
  className,
  wrapperClassName,
  ...aria
}: SelectProps) {
  const options: Option[] = Children.toArray(children)
    .filter(
      (child): child is OptionElement =>
        isValidElement(child) && child.type === "option",
    )
    .map((option) => {
      const text = textOf(option.props.children);
      return {
        value: String(option.props.value ?? text),
        label: option.props.children,
        text,
        disabled: Boolean(option.props.disabled),
      };
    });
  const controlled = value !== undefined;
  const [internal, setInternal] = useState(
    String(defaultValue ?? options[0]?.value ?? ""),
  );
  const current = controlled ? String(value) : internal;
  const selected = options.find((option) => option.value === current);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const baseId = useId();
  const listId = `${baseId}-list`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    // Up when the list wouldn't fit below and there is more room above.
    const up = below < 260 && rect.top > below;
    setPosition({
      left: rect.left,
      width: rect.width,
      ...(up
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
  }, []);

  const openList = () => {
    if (disabled) return;
    const index = options.findIndex((option) => option.value === current);
    setActive(index >= 0 ? index : 0);
    place();
    setOpen(true);
  };
  const close = () => setOpen(false);
  const choose = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    if (!controlled) setInternal(option.value);
    const target = { value: option.value, name };
    onChange?.({ target, currentTarget: target });
    close();
    triggerRef.current?.focus();
  };
  const move = (from: number, step: 1 | -1) => {
    for (let i = 1; i <= options.length; i++) {
      const next = (from + step * i + options.length) % options.length;
      if (!options[next]?.disabled) return next;
    }
    return from;
  };

  useLayoutEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`#${CSS.escape(optionId(active))}`)
      ?.scrollIntoView({ block: "nearest" });
  });
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      )
        close();
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, place]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => move(index, 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => move(index, -1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(move(-1, 1));
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(move(0, -1));
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(active);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close();
    } else if (event.key.length === 1) {
      // Type-ahead: letters typed within half a second build one query.
      const now = Date.now();
      const query =
        (now - typed.current.at < 500 ? typed.current.text : "") +
        event.key.toLowerCase();
      typed.current = { text: query, at: now };
      const match = options.findIndex(
        (option) =>
          !option.disabled && option.text.toLowerCase().startsWith(query),
      );
      if (match >= 0) setActive(match);
    }
  };

  const placeholder = selected && selected.value === "";
  return (
    <span className={cn("relative flex w-full", wrapperClassName)}>
      {name && <input type="hidden" name={name} value={current} />}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? optionId(active) : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        {...aria}
        className={cn(
          inputClass,
          "flex cursor-pointer items-center justify-between gap-2 text-left",
          open && "border-surface",
          className,
        )}
      >
        <span
          className={cn("min-w-0 truncate", placeholder && "text-ink-soft")}
        >
          {selected?.label ?? " "}
        </span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={cn(
            "shrink-0 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>
      {open &&
        position &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: "fixed",
              left: position.left,
              minWidth: position.width,
              top: position.top,
              bottom: position.bottom,
            }}
            className="border-ink bg-paper border-chip rounded-control shadow-brutal z-[60] max-h-72 max-w-[min(28rem,calc(100vw-1rem))] overflow-y-auto overscroll-contain p-1"
          >
            {options.map((option, index) => {
              const isSelected = option.value === current;
              return (
                <li
                  key={`${option.value}-${index}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  onMouseEnter={() => setActive(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(index)}
                  className={cn(
                    "flex min-h-10 cursor-pointer items-center justify-between gap-3 rounded-[4px] px-3 py-2 text-sm",
                    index === active && "bg-lemon/45",
                    isSelected && "font-display font-extrabold",
                    option.value === "" && "text-ink-soft",
                    option.disabled && "cursor-not-allowed opacity-40",
                  )}
                >
                  <span className="min-w-0">{option.label}</span>
                  {isSelected && (
                    <Check size={15} strokeWidth={3} aria-hidden="true" />
                  )}
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </span>
  );
}
